import DeleteRoundedIcon from '@mui/icons-material/DeleteRounded';
import {
  Alert,
  Box,
  CircularProgress,
  Divider,
  IconButton,
  MenuItem,
  Select,
  SelectChangeEvent,
  Typography,
} from '@mui/material';
import { DataGrid, GridColDef } from '@mui/x-data-grid';
import { useCallback, useContext, useEffect, useState } from 'react';
import { useOutletContext } from 'react-router-dom';
import MessagesApi from '../../../../apis/MessagesApi';
import ConfirmDialog from '../../../../components/ConfirmDialog/ConfirmDialog';
import LoadingCircle from '../../../../components/LoadingCircle/LoadingCircle';
import UnexpectedError from '../../../../components/UnexpectedError/UnexpectedError';
import SnackbarContext from '../../../../contexts/SnackbarContext';
import { AdminMessageDto } from '../../../../generated-types/adminMessageDto';
import { MessageState } from '../../../../generated-types/messageState';
import useApi from '../../../../lib/useApi';
import MessageDialog from './components/MessageDialog';
import MessagesGridToolbar from './components/MessagesGridToolbar';

/**
 * A view to display messages from the users
 */
const MessagesSubview = () => {
  const messagesApi = useApi(MessagesApi);
  const [receivedMessages, setReceivedMessages] = useState<AdminMessageDto[]>(
    []
  );
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const snackbar = useContext(SnackbarContext);
  const [selectedMessage, setSelectedMessage] =
    useState<AdminMessageDto | null>(null);
  const [messageToDeleteId, setMessageToDeleteId] = useState<string>('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [errorDialog, setErrorDialog] = useState(false);
  const [updatingStateIds, setUpdatingStateIds] = useState<string[]>([]);
  const { unreadMessages, setUnreadMessages } = useOutletContext<{
    unreadMessages: number;
    setUnreadMessages: React.Dispatch<React.SetStateAction<number>>;
  }>();

  /**
   * Method to fetch the messages
   */
  const fetchMessages = useCallback(() => {
    setLoading(true);
    messagesApi
      .GetMessages()
      .then(setReceivedMessages)
      .catch(() => {
        setError(true);
        snackbar.setAlert(
          <Alert variant="filled" severity="error">
            An error occurred while loading the messages
          </Alert>
        );
        snackbar.triggerSnackbar();
      })
      .finally(() => setLoading(false));
  }, [messagesApi, snackbar]);

  /**
   * Load the messages from the database
   */
  useEffect(() => {
    fetchMessages();
  }, [fetchMessages]);

  /**
   * Trigger a refresh whenever the unreadMessages count changes
   */
  useEffect(() => {
    fetchMessages();
  }, [unreadMessages, fetchMessages]);

  /**
   * Convert the message states to human readable states
   *
   * Note: There are parts of the code that rely on the exact text and index positions
   *  of this array. Use caution if changed.
   */
  const messageStates = ['Unread', 'Replied', 'Resolved', 'No Action Needed'];

  /**
   * Open the dialog when a row is clicked
   */
  const handleRowClick = (params: any) => {
    setSelectedMessage(params.row);
    setDialogOpen(true);
  };

  /**
   * Close the details dialog
   */
  const handleCloseDialog = () => {
    setDialogOpen(false);
    setSelectedMessage(null);
  };

  /**
   * Delete a message from the state
   * @param id The id of the message to delete
   */
  const handleDelete = async (id: string) => {
    await messagesApi.DeleteMessage(id);
    setReceivedMessages((prev) => prev.filter((msg) => msg.id !== id));
  };

  // Define grid columns
  const columns: GridColDef[] = [
    { field: 'from', headerName: 'From', flex: 1 },
    {
      field: 'message',
      headerName: 'Message',
      flex: 2,
      // Render a preview of the message (first 50 characters)
      renderCell: (params) => {
        const fullMessage = params.value;
        const preview =
          fullMessage.length > 50
            ? fullMessage.substring(0, 50) + '...'
            : fullMessage;
        return <span>{preview}</span>;
      },
    },
    {
      field: 'received',
      headerName: 'Received',
      flex: 1,
      // Format the date for readability
      valueFormatter: (params) => new Date(params.value).toLocaleString(),
    },
    {
      field: 'state',
      headerName: 'State',
      flex: 1,
      renderCell: (params) => {
        // If this row is currently updating, show a spinner
        if (updatingStateIds.includes(params.row.id)) {
          return (
            <Box width="100%" display="flex" justifyContent="center">
              <CircularProgress size={20} />
            </Box>
          );
        }

        // Setup state change handling
        const currentStateIndex = params.row.state;
        const handleStateChange = async (event: SelectChangeEvent<string>) => {
          // Setup
          const newStateText = event.target.value as string;
          const newStateIndex = messageStates.indexOf(newStateText);
          const previousState = receivedMessages.filter(
            (m) => m.id == params.row.id
          )[0].state;
          setUpdatingStateIds((prev) => [...prev, params.row.id]);

          try {
            // Change the state
            const updatedBy = await messagesApi.ChangeState(
              params.row.id,
              newStateIndex
            );
            setReceivedMessages((prev) =>
              prev.map((msg) =>
                msg.id === params.row.id
                  ? { ...msg, state: newStateIndex, lastChangedBy: updatedBy }
                  : msg
              )
            );

            // Update the state prior to the polling
            const unreadIndex = messageStates.indexOf('Unread');

            // If the previous state was unread and the new state is not unread, that
            //  means the message is now read.
            if (
              previousState == MessageState.Unread &&
              newStateIndex != unreadIndex
            ) {
              setUnreadMessages((p) => Number(p) - 1);
            }
            // If the previous state was read and the new state is unread, that means the
            //  admin is setting the message as unread
            else if (
              previousState != MessageState.Unread &&
              newStateIndex == unreadIndex
            ) {
              setUnreadMessages((p) => Number(p) + 1);
            }
          } catch {
            snackbar.setAlert(
              <Alert variant="filled" severity="error">
                Failed to update message state.
              </Alert>
            );
            snackbar.triggerSnackbar();
          } finally {
            setUpdatingStateIds((prev) =>
              prev.filter((id) => id !== params.row.id)
            );
          }
        };

        return (
          <Select
            value={messageStates[currentStateIndex]}
            onChange={handleStateChange}
            variant="standard"
            onClick={(e) => e.stopPropagation()} // Prevent row click when using dropdown
            sx={{
              width: '100%',
            }}
          >
            {messageStates.map((state) => (
              <MenuItem key={state} value={state}>
                {state}
              </MenuItem>
            ))}
          </Select>
        );
      },
    },
    {
      field: 'actions',
      headerName: 'Actions',
      flex: 0.5,
      sortable: false,
      filterable: false,
      // Render a delete button in each row
      renderCell: (params) => (
        <IconButton
          color="error"
          onClick={(e) => {
            e.stopPropagation();
            setMessageToDeleteId(params.row.id);
            setErrorDialog(true);
          }}
        >
          <DeleteRoundedIcon />
        </IconButton>
      ),
    },
  ];

  return (
    <Box height="100%" display="flex" flexDirection="column">
      {error ? (
        <UnexpectedError errorContext="the messages" />
      ) : (
        <>
          <Box p={1.5}>
            <Typography variant="h4">Messages</Typography>
          </Box>
          <Divider />
          <Box height="100%" overflow="auto" padding="1em">
            <DataGrid
              rows={receivedMessages}
              columns={columns}
              rowSelection={false}
              getRowId={(row) => row.id}
              density="compact"
              initialState={{
                sorting: {
                  sortModel: [{ field: 'received', sort: 'desc' }],
                },
              }}
              onRowClick={handleRowClick}
              slots={{
                toolbar: MessagesGridToolbar,
              }}
              slotProps={{
                toolbar: {
                  onRefreshClick: fetchMessages,
                },
              }}
              sx={{
                height: '100%',
                minWidth: '800px',
                '& .MuiDataGrid-row': {
                  cursor: 'pointer',
                },
                '& .unread-row': {
                  fontWeight: 'bold',
                  backgroundColor: (t) =>
                    t.palette.mode == 'light'
                      ? 'initial'
                      : 'rgba(255, 255, 255, 0.1)',
                },
                '& .read-row': {
                  backgroundColor: (t) =>
                    t.palette.mode == 'light'
                      ? 'rgba(0, 0, 0, 0.04)'
                      : 'initial',
                },
              }}
              getRowClassName={(params) =>
                params.row.state === 0 ? 'unread-row' : 'read-row'
              }
            />
          </Box>
          {/* Dialog to show full message details */}
          <MessageDialog
            dialogOpen={dialogOpen}
            handleCloseDialog={handleCloseDialog}
            messageStates={messageStates}
            selectedMessage={selectedMessage}
          />
          {/* Dialog to confirm that the user wants to delete the message */}
          <ConfirmDialog
            dialogTitle="Confirm Deletion"
            dialogBody="Are you sure you want to delete the message? This cannot be undone."
            successMessage="Successfully deleted the message."
            errorMessage="An error occurred while trying to delete the message. Please try again."
            showDialog={errorDialog}
            onClose={() => setErrorDialog(false)}
            confirmText="Delete"
            confirmAction={async () => handleDelete(messageToDeleteId)}
            confirmColor="error"
          />
        </>
      )}
      <LoadingCircle isLoading={loading} />
    </Box>
  );
};

export default MessagesSubview;
